import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import E3Combobox from "./E3Combobox";
import E3DateTime from "./E3DateTime";
import styles from "./E3DateRange.module.css";
import * as dateFns from "date-fns";

/**
 * E3DateRange
 * @type React.FC<E3DateRangePropTypes>
 */
const E3DateRange = React.forwardRef((props, ref) => {
  const refCombobox = useRef();
  const refDateTime1 = useRef();
  const refDateTime2 = useRef();

  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3DateRangePropTypes).includes(p)) {
      console.error(`E3DateRange: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    clear() {
      refDateTime1.current?.setValue(props.defaultStartDate ?? dateFns.toDate(Date.now()));
      refDateTime2.current?.setValue(props.defaultEndDate ?? dateFns.toDate(Date.now()));
      if (currState.showCombobox) {
        refCombobox.current?.clear();
        if (props.comboBoxDefaultValue && props.comboBoxDefaultValue == 9) {
          refDateTime1.current?.enable();
          refDateTime2.current?.enable();
        } else {
          refDateTime1.current?.disable();
          refDateTime2.current?.disable();
        }
      } else {
        refDateTime1.current?.enable();
        refDateTime2.current?.enable();
      }
    },
    setStartDate(value) {
      _setStartDate(value);
    },
    setMinDate(refdateTime,value) {
      if(refdateTime === "refDateTime1"){
        refDateTime1.current?.updateState("minDate", value)
      }else if(refdateTime === "refDateTime1"){
        refDateTime2.current?.updateState("minDate", value)
      }
    },    
    setMaxDate(refdateTime,value) {
      if(refdateTime === "refDateTime1"){
        refDateTime1.current?.updateState("maxDate", value)
      }else if(refdateTime === "refDateTime2"){
        refDateTime2.current?.updateState("maxDate", value)
      }
    },        

    setEndDate(value) {
      _setEndDate(value);
    },

    setDay(value = 0) {
      refCombobox.current?.setValue(value);
    },

    getStartDate() {
      return refDateTime1.current?.getValue();
    },

    getEndDate() {
      return refDateTime2.current?.getValue();
    },

    enable() {
      refDateTime1.current?.enable();
      refDateTime2.current?.enable();
    },

    disable() {
      refDateTime1.current?.disable();
      refDateTime2.current?.disable();
    },

    /** 값 가져오기 */
    getValue() {
      return {
        startDate: refDateTime1.current?.getValue(),
        endDate: refDateTime2.current?.getValue(),
      };
    },

    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error("E3DateRange: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다.");
      }
    },
  }));

  /**
   * E3DateRange Component의 State
   */
  const [currState, setCurrState] = useState({
    showCombobox: props.showCombobox ? true : false,
    datePickerEnabled: props.showCombobox
      ? props.comboBoxDefaultValue === 9
        ? false
        : props.datePickerEnabled
      : false,
  });

  useEffect(() => {
    const cboData = [
      {
        codeId: 0,
        codeName: "오늘",
      },
      {
        codeId: 1,
        codeName: "어제",
      },
      {
        codeId: 2,
        codeName: "내일",
      },
      {
        codeId: 3,
        codeName: "이번주",
      },
      {
        codeId: 4,
        codeName: "지난주",
      },
      {
        codeId: 5,
        codeName: "다음주",
      },
      {
        codeId: 6,
        codeName: "이번달",
      },
      {
        codeId: 7,
        codeName: "지난달",
      },
      {
        codeId: 8,
        codeName: "다음달",
      },
      {
        codeId: 9,
        codeName: "직접지정",
      },
    ];
    refCombobox.current?.setDataBinding(cboData);
  }, []);

  const _onComboChange = (e) => {
    switch (Number(e.target.value)) {
      case 0: // 금일
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(Date.now()));
        refDateTime2.current?.setValue(dateFns.toDate(Date.now()));
        break;
      case 1: // 전일
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.subDays(new Date(), 1)));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.subDays(new Date(), 1)));
        break;
      case 2: // 익일
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.addDays(new Date(), 1)));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.addDays(new Date(), 1)));
        break;
      case 3: // 금주
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.startOfISOWeek(new Date())));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.addDays(dateFns.startOfISOWeek(new Date()), 6)));

        break;
      case 4: // 전주
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.addDays(dateFns.startOfISOWeek(new Date()), -7)));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.addDays(dateFns.startOfISOWeek(new Date()), -1)));

        break;
      case 5: // 다음주
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.addDays(dateFns.startOfISOWeek(new Date()), 7)));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.addDays(dateFns.startOfISOWeek(new Date()), 13)));

        break;
      case 6: // 금월
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.startOfMonth(new Date())));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.endOfMonth(new Date())));

        break;
      case 7: // 전월
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.startOfMonth(dateFns.subMonths(new Date(), 1))));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.endOfMonth(dateFns.subMonths(new Date(), 1))));

        break;
      case 8: // 익월
        refDateTime1.current?.disable();
        refDateTime2.current?.disable();
        refDateTime1.current?.setValue(dateFns.toDate(dateFns.startOfMonth(dateFns.addMonths(new Date(), 1))));
        refDateTime2.current?.setValue(dateFns.toDate(dateFns.endOfMonth(dateFns.addMonths(new Date(), 1))));

        break;
      case 9: // 기간
        refDateTime1.current?.enable();
        refDateTime2.current?.enable();
        refDateTime1.current?.setMaxDate(dateFns.toDate(dateFns.parseISO(refDateTime2.current?.getValue())));
        refDateTime2.current?.setMinDate(dateFns.toDate(dateFns.parseISO(refDateTime1.current?.getValue())));
        break;
      default:
        break;
    }
  };

  const _onStartDateChange = (e) => {
    _setStartDate(e);
    if (props.onChange) {
      props.onChange({
        startDate: e,
        endDate: new Date(refDateTime2.current.getValue()),
      });
    }
  };

  const _onEndDateChange = (e) => {
    _setEndDate(e);
    if (props.onChange) {
      props.onChange({
        startDate: new Date(refDateTime1.current.getValue()),
        endDate: e,
      });
    }
  };

  const _setStartDate = (value) => {
    if (typeof value === "string") {
      value = dateFns.parse(value, props.dateFormat, new Date());
    }
    refCombobox.current?.setValue(9);
    refDateTime1.current?.setStartDate(value);
    refDateTime2.current?.setStartDate(value);
    refDateTime2.current?.setMinDate(value);
    refDateTime1.current?.setValue(value);
    refDateTime1.current?.enable();
    refDateTime2.current?.enable();
  };

  const _setEndDate = (value) => {
    if (typeof value === "string") {
      value = dateFns.parse(value, props.dateFormat, new Date());
    }
    refCombobox.current?.setValue(9);
    refDateTime1.current?.setEndDate(value);
    refDateTime2.current?.setEndDate(value);
    refDateTime1.current?.setMaxDate(value);
    refDateTime2.current?.setValue(value);
    refDateTime1.current?.enable();
    refDateTime2.current?.enable();
  };

  return (
    // <div className={`${styles.e3DateRange} ${currState.showCombobox ? 'wpx_350' : 'wpx_250'}`}>
    <div className={`${styles.e3DateRange} ${currState.showCombobox ? "wpct_100" : "wpct_100"}`}>
      <div className={styles.comboBox} hidden={!currState.showCombobox}>
        <E3Combobox
          ref={refCombobox}
          // controlWidth={'wpx_100'}
          onChange={_onComboChange}
          defaultValue={props.comboBoxDefaultValue}
        />
      </div>
      <div className={currState.showCombobox ? styles.dateBoxWithCombo : styles.dateBox}>
        <E3DateTime
          ref={refDateTime1}
          {...(props.timeIntervals && { timeIntervals: props.timeIntervals })}
          defaultValue={props.defaultStartDate}
          selectsStart={true}
          disabled={currState.datePickerEnabled}
          onChange={_onStartDateChange}
          showMonthYearPicker={props.showMonthYearPicker}
          showYearPicker={props.showYearPicker}
          startDate={props.defaultStartDate}
          endDate={props.defaultEndDate}
          dateFormat={props.dateFormat}
          maxDate={props.startMaxDate}
          minDate={props.startMinDate}
          showTimeSelectOnly={props.showTimeSelectOnly}
          showTimeSelect={props.showTimeSelect}
          showClearButton={props.showStartClearButton}
          // controlWidth={props.showStartClearButton ? 'wpx_140' : 'wpx_115'}
        />
      </div>
      {props.showSeparator ? <span>~</span> : null}
      <div className={currState.showCombobox ? styles.dateBoxWithCombo : styles.dateBox}>
        <E3DateTime
          ref={refDateTime2}
          {...(props.timeIntervals && { timeIntervals: props.timeIntervals })}
          defaultValue={props.defaultEndDate}
          selectsEnd={true}
          disabled={currState.datePickerEnabled}
          onChange={_onEndDateChange}
          showMonthYearPicker={props.showMonthYearPicker}
          showYearPicker={props.showYearPicker}
          showTimeSelectOnly={props.showTimeSelectOnly}
          showTimeSelect={props.showTimeSelect}
          startDate={props.defaultStartDate}
          endDate={props.defaultEndDate}
          dateFormat={props.dateFormat}
          maxDate={props.endMaxDate}
          minDate={props.endMinDate}
          showClearButton={props.showEndClearButton}
          // controlWidth={props.showEndClearButton ? 'wpx_140' : 'wpx_115'}
        />
      </div>
    </div>
  );
});

const E3DateRangePropTypes = {
  /** 날짜 포맷: 기본값은 {'yyyy-MM-dd'}. */
  dateFormat: PropTypes.string,

  /** 사전 정의되어 있는 기간 선택 콤보박스 표기: 기본값은 {true}. */
  showCombobox: PropTypes.bool,

  /** Date 선택 가능 여부: 기본값은 {false}.
   * 기간 선택 콤보박스가 비활성화 되어 있는 경우, prop과 관계없이 활성화
   */
  datePickerEnabled: PropTypes.bool,

  /** 기본 시작일시: 기본값은 {new Date()}.
   * Date() 오브젝트.
   * 이후 setStartDate()로 제어 가능 */
  defaultStartDate: PropTypes.object,

  /** 기본 종료일시: 기본값은 {new Date()}.
   * Date() 오브젝트.
   * 이후 setEndDate()로 제어 가능 */
  defaultEndDate: PropTypes.object,

  /** 구분기호 (~) 표기여부: 기본값은 {false}. */
  showSeparator: PropTypes.bool,

  /** 시작일시의 선택가능 최대값: 기본값은 {null}.
   * Date() 오브젝트.*/
  startMaxDate: PropTypes.object,

  /** 시작일시의 선택가능 최소값: 기본값은 {null}.
   * Date() 오브젝트.*/
  startMinDate: PropTypes.object,

  /** 종료일시의 선택가능 최대값: 기본값은 {null}.
   * Date() 오브젝트.*/
  endMaxDate: PropTypes.object,

  /** 종료일시의 선택가능 최소값: 기본값은 {null}.
   * Date() 오브젝트.*/
  endMinDate: PropTypes.object,

  /** 시작일시의 제거버튼 표기: 기본값은 {false}. */
  showStartClearButton: PropTypes.bool,

  /** 종료일시의 제거버튼 표기: 기본값은 {false}. */
  showEndClearButton: PropTypes.bool,

  /** 월 단위 달력 활성화 여부 */
  showMonthYearPicker: PropTypes.bool,

  /** 연 단위 달력 활성화 여부 */
  showYearPicker: PropTypes.bool,

  /** onChange 함수: 기본값은 {null}. */
  onChange: PropTypes.func,

  /** combo Box 초기 선택 항목 : 기본값은 {0}. */
  comboBoxDefaultValue: PropTypes.number,
  onEnterPress: PropTypes.func,

  /** 시간 선택 시 interval */
  timeIntervals: PropTypes.number,
};

E3DateRange.propTypes = E3DateRangePropTypes;
E3DateRange.defaultProps = {
  dateFormat: "yyyy-MM-dd",
  showCombobox: true,
  datePickerEnabled: true,
  defaultStartDate: new Date(),
  defaultEndDate: new Date(),
  showSeparator: false,
  startMaxDate: null,
  startMinDate: null,
  endMaxDate: null,
  endMinDate: null,
  timeIntervals: 1,
  showStartClearButton: false,
  showEndClearButton: false,
  showMonthYearPicker: false,
  showYearPicker: false,
  onChange: null,
  comboBoxDefaultValue: 9,
};
E3DateRange.displayName = "E3DateRange";
export default E3DateRange;
